import { useMemo, useState } from "react";
import { Banknote, Ban, Undo2, CheckCircle2, RotateCcw } from "lucide-react";
import { Modal, SelectField, Field } from "../lib/ui";
import { today, rowLink } from "../lib/api";
import { Plus } from "lucide-react";
import { Page, Panel, Table, DeleteButton, StatusPill, SearchInput } from "../lib/ui";
import { money, can, humanDate } from "../lib/api";

const DONE = ["Tahsil Edildi", "Ödendi", "Ciro Edildi", "Karşılıksız", "İade", "İptal"];

function ChequeActionModal({ cheque, action, accounts, onClose, onDone }) {
  const [accountId, setAccountId] = useState("");
  const [date, setDate] = useState(today());
  const [err, setErr] = useState("");
  const collect = action === "collect";
  const go = async () => {
    try { await onDone(cheque, action, { account_id: accountId, date }); onClose(); }
    catch (e) { setErr(e.response?.data?.detail || "İşlem yapılamadı"); }
  };
  return (
    <Modal title={collect ? "Çek tahsil et" : "Çek ödendi"} subtitle={`${cheque.check_no || "—"} · ${cheque.customer_name} · ${money(cheque.amount)}`}
           onClose={onClose} testid="cheque-action-modal">
      <div className="form-grid two">
        <SelectField testid="cheque-action-account" label={collect ? "Paranın girdiği kasa / banka" : "Paranın çıktığı banka"} value={accountId}
                     onChange={(e) => setAccountId(e.target.value)}>
          <option value="">— Hesap seçin —</option>
          {accounts.filter((a) => !a.off_balance).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </SelectField>
        <Field testid="cheque-action-date" label="Tarih" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      {err && <div className="form-error" data-testid="cheque-action-error">{err}</div>}
      <div className="modal-actions">
        <button type="button" className="secondary-button" onClick={onClose}>Vazgeç</button>
        <button type="button" className="primary-button" data-testid="cheque-action-submit" disabled={!accountId} onClick={go}>Onayla</button>
      </div>
    </Modal>
  );
}

function ChequeActions({ c, user, onAsk, onAction }) {
  const confirmDo = (action, msg) => window.confirm(msg) && onAction(c, action).catch((e) => alert(e.response?.data?.detail || "İşlem yapılamadı"));
  if (["Karşılıksız", "İade"].includes(c.status) && can(user, "edit_cheque")) return (
    <div className="cheque-actions">
      <StatusPill status={c.status} />
      <button className="row-icon tone-muted" title="Portföye geri al" data-testid={`cheque-reopen-${c.id}`}
              onClick={() => confirmDo("reopen", `Çek ${c.check_no || ""} "${c.status}" durumundan çıkarılıp tekrar Portföyde yapılsın mı?`)}><RotateCcw size={14} /></button>
    </div>
  );
  if (c.status !== "Portföyde" || !can(user, "edit_cheque")) return <StatusPill status={c.status} />;
  const ownGiven = c.direction === "given" && !c.endorsed_from;
  return (
    <div className="cheque-actions">
      <StatusPill status={c.status} />
      {c.direction === "received" && (
        <button className="row-icon tone-success" title="Tahsil et" data-testid={`cheque-collect-${c.id}`} onClick={() => onAsk(c, "collect")}><Banknote size={14} /></button>
      )}
      {c.direction === "given" && (
        <button className="row-icon tone-success" title="Ödendi" data-testid={`cheque-pay-${c.id}`}
                onClick={() => (ownGiven ? onAsk(c, "pay") : confirmDo("pay", "Ciro edilen çek ödendi olarak işaretlensin mi?"))}><CheckCircle2 size={14} /></button>
      )}
      <button className="row-icon tone-danger" title="Karşılıksız" data-testid={`cheque-bounce-${c.id}`}
              onClick={() => confirmDo("bounce", "Çek karşılıksız olarak işaretlensin mi? Cari bakiyesinden düşülen tutar geri eklenir.")}><Ban size={14} /></button>
      <button className="row-icon tone-muted" title="İade" data-testid={`cheque-return-${c.id}`}
              onClick={() => confirmDo("return", "Çek iade edilsin mi? Cari bakiyesindeki etkisi kaldırılır.")}><Undo2 size={14} /></button>
    </div>
  );
}

export default function Cheques({ rows, setModal, onDelete, onAction, accounts = [], user }) {
  const [ask, setAsk] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const filtered = useMemo(() => rows.filter((c) => {
    if (tab !== "all" && c.direction !== tab) return false;
    if (!q) return true;
    return (c.check_no || "").includes(q) || (c.customer_name || "").toLowerCase().includes(q.toLowerCase());
  }), [rows, q, tab]);

  const totals = useMemo(() => {
    const received = rows.filter((c) => c.direction === "received").reduce((s, c) => s + c.amount, 0);
    const given = rows.filter((c) => c.direction === "given").reduce((s, c) => s + c.amount, 0);
    return { received, given, balance: received - given };
  }, [rows]);

  return (
    <>
      <Page
        eyebrow="ÇEK TAKİBİ"
        title="Çekler"
        subtitle="Alınan ve verilen çekleri vade ve durumlarıyla izleyin."
        action={
          <button data-testid="add-cheques-button" className="primary-button" disabled={!can(user, "create_cheque")}
                  title={!can(user, "create_cheque") ? "Bu işlem için yetkiniz yok" : ""}
                  onClick={() => setModal({ type: "cheque" })}>
            <Plus size={17} /> Yeni çek
          </button>
        }
      />

      <div className="mini-stats">
        <div><span>Alınan çekler</span><strong className="positive">+{money(totals.received)}</strong></div>
        <div><span>Verilen çekler</span><strong className="negative">-{money(totals.given)}</strong></div>
        <div><span>Net çek bakiyesi</span><strong>{money(totals.balance)}</strong></div>
      </div>

      <Panel
        className="module-panel"
        action={
          <div className="filter-row">
            <div className="tab-group">
              {[["all", "Tümü"], ["received", "Alınan"], ["given", "Verilen"]].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`cheques-tab-${id}`}
                  className={tab === id ? "tab active" : "tab"}
                  onClick={() => setTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <SearchInput value={q} onChange={setQ} testid="cheques-search" placeholder="Çek no veya cari" />
          </div>
        }
      >
        <Table
          testid="cheques-table"
          columns={[
            { label: "Çek no" }, { label: "Yön" }, { label: "Cari" }, { label: "Banka" },
            { label: "Vade" }, { label: "Durum" }, { label: "Tutar", align: "right" }, { label: "" },
          ]}
          rows={[...filtered].sort((a, b) => (DONE.includes(a.status) - DONE.includes(b.status))
            || String(a.maturity_date || "").localeCompare(String(b.maturity_date || "")))}
          empty="Henüz çek kaydedilmedi."
          renderRow={(c) => (
            <tr key={c.id} data-testid={`cheque-row-${c.id}`} className="clickable-row"
                {...rowLink(`/detay/cek/${c.id}`)}
                style={{ cursor: "pointer" }}>
              <td><a href={`/detay/cek/${c.id}`} className="row-anchor" onClick={(e) => e.stopPropagation()}><strong>{c.check_no || "—"}</strong></a><small>{humanDate(c.issue_date)}</small></td>
              <td>
                <span className={`direction ${c.direction === "received" ? "in" : "out"}`}>
                  {c.direction === "received" ? "Alınan" : "Verilen"}
                </span>
              </td>
              <td>{c.customer_name || "—"}</td>
              <td>{c.bank_name || "—"}<small>{c.branch}</small></td>
              <td>{humanDate(c.maturity_date)}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <div data-testid={`cheque-status-${c.id}`}>
                  <ChequeActions c={c} user={user} onAction={onAction} onAsk={(ch, action) => setAsk({ cheque: ch, action })} />
                </div>
              </td>
              <td className={`amount ${c.direction === "received" ? "positive" : "negative"}`}>
                {c.direction === "received" ? "+" : "-"}{money(c.amount)}
              </td>
              <td className="amount" onClick={(e) => e.stopPropagation()}>
                <DeleteButton
                  testid={`delete-cheque-${c.id}`}
                  disabled={!can(user, "delete_cheque")}
                  onClick={() => onDelete(c)}
                />
              </td>
            </tr>
          )}
        />
      </Panel>
      {ask && <ChequeActionModal cheque={ask.cheque} action={ask.action} accounts={accounts}
                                 onClose={() => setAsk(null)} onDone={onAction} />}
    </>
  );
}
